import * as THREE from 'three'

import { LayerOrders } from '../../shared/shared'

const DEFAULT_FPS = 4
const MAX_SHADOWS = 512

export class ClientShadows {
  constructor(client) {
    this.client = client
    this.atlas = client.spriteAtlas
    this.byId = new Map()
    this.nextId = 0
    this._freeSlots = []
    this._highWater = 0

    const geo = new THREE.PlaneGeometry(1, 1)
    geo.translate(0, 0.5, 0) // bottom-center pivot, consistent with tiles/sprites/avatars

    const uvArr = new Float32Array(MAX_SHADOWS * 4)
    const animArr = new Float32Array(MAX_SHADOWS * 3) // frameCount, fps, strideU
    geo.setAttribute('aUvRect', new THREE.InstancedBufferAttribute(uvArr, 4))
    geo.setAttribute('aAnimData', new THREE.InstancedBufferAttribute(animArr, 3))

    this._uvArr = uvArr
    this._animArr = animArr
    this._dummy = new THREE.Object3D()

    const mat = this._createMaterial()
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_SHADOWS)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.client.scene.add(this.mesh)

    this.atlas.onReady(() => {
      this.mesh.material.uniforms.uAtlas.value = this.atlas.texture
    })
  }

  _createMaterial() {
    return new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: { value: this.atlas.texture },
        uTime: { value: 0 },
        uZ: { value: LayerOrders.SHADOW },
      },
      vertexShader: /* glsl */ `
        precision highp float;
        attribute vec4 aUvRect;
        attribute vec3 aAnimData;
        uniform float uTime;
        uniform float uZ;
        varying vec2 vUv;
        void main() {
          float frameCount = aAnimData.x;
          float fps = aAnimData.y;
          float strideU = aAnimData.z;
          float frame = 0.0;
          if (frameCount > 1.0 && fps > 0.0) {
            frame = floor(mod(uTime * fps, frameCount));
          }
          vec2 mn = vec2(aUvRect.x + frame * strideU, aUvRect.y);
          vUv = mix(mn, mn + aUvRect.zw, uv);
          vec4 worldPos = instanceMatrix * vec4(position.xy, 0.0, 1.0);
          gl_Position = projectionMatrix * viewMatrix * vec4(worldPos.xy, uZ, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uAtlas;
        varying vec2 vUv;
        void main() {
          vec4 c = texture2D(uAtlas, vUv);
          if (c.a < 0.01) discard;
          gl_FragColor = c;
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
    })
  }

  _allocSlot() {
    if (this._freeSlots.length > 0) return this._freeSlots.pop()
    if (this._highWater < MAX_SHADOWS) {
      const slot = this._highWater++
      this.mesh.count = this._highWater
      return slot
    }
    return -1
  }

  _freeSlot(slot) {
    this._dummy.scale.set(0, 0, 0)
    this._dummy.updateMatrix()
    this.mesh.setMatrixAt(slot, this._dummy.matrix)
    this.mesh.instanceMatrix.needsUpdate = true
    this._dummy.scale.set(1, 1, 1)
    this._freeSlots.push(slot)
  }

  add(opts) {
    const slot = this._allocSlot()
    if (slot === -1) return null
    const id = this.nextId++
    const handle = new ShadowHandle(this, id, slot, opts)
    this.byId.set(id, handle)
    return handle
  }

  update(delta, time) {
    this.mesh.material.uniforms.uTime.value = time
  }

  destroy() {
    for (const handle of this.byId.values()) {
      handle.destroy()
    }
    this.client.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
  }
}

class ShadowHandle {
  constructor(shadows, id, slot, opts) {
    this.shadows = shadows
    this.id = id
    this.slot = slot
    this.atlas = shadows.atlas
    this._x = opts.x || 0
    this._y = opts.y || 0
    this._w = opts.width || 1
    this._h = opts.height || 1
    this._yCenter = opts.yCenter || 0
    this.fps = opts.fps
    this.url = opts.url

    if (opts.url) {
      this._resolve(opts.url)
    }

    this._onReady = url => {
      if (url === this.url) this._updateUVs()
    }
    this.atlas.onReady(this._onReady)

    this._updateMatrix()
  }

  _resolve(url) {
    this.atlas.resolve(url, this._w, this._h)
    this._updateUVs()
  }

  _updateMatrix() {
    const d = this.shadows._dummy
    d.position.set(this._x, this._y - this._yCenter, 0)
    d.scale.set(this._w, this._h, 1)
    d.updateMatrix()
    this.shadows.mesh.setMatrixAt(this.slot, d.matrix)
    this.shadows.mesh.instanceMatrix.needsUpdate = true
  }

  _updateUVs() {
    const uv = this.atlas.getUV(this.url)
    if (!uv) return

    const fps = this.fps != null ? this.fps : uv.frameCount > 1 ? DEFAULT_FPS : 0
    const i4 = this.slot * 4
    this.shadows._uvArr[i4] = uv.u
    this.shadows._uvArr[i4 + 1] = uv.v
    this.shadows._uvArr[i4 + 2] = uv.frameW
    this.shadows._uvArr[i4 + 3] = uv.frameH

    const i3 = this.slot * 3
    this.shadows._animArr[i3] = uv.frameCount
    this.shadows._animArr[i3 + 1] = fps
    this.shadows._animArr[i3 + 2] = uv.strideU

    this.shadows.mesh.geometry.attributes.aUvRect.needsUpdate = true
    this.shadows.mesh.geometry.attributes.aAnimData.needsUpdate = true
  }

  setPosition(x, y) {
    this._x = x
    this._y = y
    this._updateMatrix()
  }

  setSize(w, h) {
    this._w = w
    this._h = h
    this._updateMatrix()
  }

  destroy() {
    this.shadows._freeSlot(this.slot)
    this.shadows.byId.delete(this.id)
    this.atlas.offReady(this._onReady)
  }
}
